import { formatDateToISO } from "../../../utils/helpers";
import "./InputField.css";

import DoneIcon from "../../../assets/done.svg?react";
import CancelIcon from "../../../assets/cancel.svg?react";
import { useState } from "react";
import Loader from "../Loader/Loader";

interface InputFieldProps {
    inputFieldId: string;
    label: string;
    value: string | number | undefined;
    type: string;
    className?: string;
    labelClassName?: string;
    onChange: (value: string) => void;
    onBlur?: (value: string) => Promise<boolean | undefined> | void;
    onFocus?: (value: string) => void;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
    readonly?: boolean;
    isTextArea?: boolean;
    textAreaRows?: number;
    w100?: boolean;
    hasError?: boolean;
    placeholder?: string;
    min?: number;
    max?: number;
    step?: string;
    maxLength?: number;
    valueSuffix?: string;
    initialValue?: string | number;
    disabled?: boolean;
    isSaving?: boolean;
    errorMessage?: string;
}

const InputField = (props: InputFieldProps) => {
    // If initialValue is provided, use it as the default value but only if value is not already set.
    const initialValue = props.initialValue ?? "";
    const value = props.value ?? initialValue;

    const [savedState, setSavedState] = useState<boolean | undefined>(undefined);

    const showSavedStateForInterval = (state: boolean | undefined) => {
        setSavedState(state);
        if (state === undefined) return;
        setTimeout(() => {
            setSavedState(undefined);
        }, 5000);
    };

    const numberInputOnWheelPreventChange = (e: React.WheelEvent<HTMLInputElement>) => {
        // Prevent the input value change
        (e.target as HTMLInputElement).blur();
        // Refocus immediately, on the next tick (after the current function is done)
        setTimeout(() => {
            (e.target as HTMLInputElement).focus();
        }, 0);
    };

    return (
        <label
            htmlFor={props.inputFieldId}
            className={`input-field-label ${props.labelClassName ?? ""} ${props.w100 ? "w-100" : ""}`}
        >
            {props.label}
            <div
                className={`input-field-container ${props.className ?? ""} ${props.readonly ? "readonly" : ""} ${
                    props.valueSuffix ?? ""
                } ${props.hasError ? "error" : ""}`}
            >
                {!props.isTextArea && (
                    <>
                        <input
                            id={props.inputFieldId}
                            readOnly={props.readonly}
                            placeholder={props.placeholder}
                            onChange={(event) => {
                                const value = event.target.value;
                                if (
                                    props.type === "number" &&
                                    /[a-zA-ZæøåÆØÅ]/.test((event.nativeEvent as InputEvent).data ?? "")
                                ) {
                                    event.stopPropagation();
                                    return;
                                }
                                props.onChange(value);
                            }}
                            onWheel={numberInputOnWheelPreventChange}
                            onFocus={(event) => props.onFocus?.(event.target.value)}
                            onBlur={(event) =>
                                props.onBlur?.(event.target.value)?.then((result) => showSavedStateForInterval(result))
                            }
                            onMouseEnter={props.onMouseEnter}
                            onMouseLeave={props.onMouseLeave}
                            value={props.type === "date" ? formatDateToISO(value as number) : value}
                            className="input-field"
                            type={props.type}
                            min={props.min}
                            max={props.max}
                            step={props.step ?? "any"}
                            maxLength={props.maxLength}
                            disabled={props.disabled}
                        />
                        {props.isSaving ? (
                            <Loader size={16} inline />
                        ) : (
                            savedState !== undefined && (
                                <div
                                    className="center"
                                    style={{
                                        height: "20px",
                                        width: "20px",
                                        backgroundColor: savedState ? "green" : "red",
                                        borderRadius: "25%",
                                    }}
                                >
                                    {savedState ? <DoneIcon color="white" /> : <CancelIcon color="white" />}
                                </div>
                            )
                        )}
                    </>
                )}
                {props.isTextArea && (
                    <textarea
                        id={props.inputFieldId}
                        readOnly={props.readonly}
                        onChange={(event) => props.onChange(event.target.value)}
                        value={value}
                        className="input-field"
                        rows={props.textAreaRows ?? 5}
                        maxLength={props.maxLength}
                        disabled={props.disabled}
                    />
                )}
            </div>
            <span style={{ color: "red" }}>{props.errorMessage}</span>
        </label>
    );
};

export default InputField;
